import React, {useRef, useState} from "react";
import {User, Mail, Lock, Shield, Loader, Eye, EyeOff, Monitor, Smartphone, Check, Fingerprint, Briefcase, Camera, X, Phone} from "lucide-react";
import {api, API_URL} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient, useQuery} from '@tanstack/react-query';
import BackofficeAccessModal from "./BackofficeAccessModal";
import Quickcom360AccessModal from "./Quickcom360AccessModal";
import {MiniColorPicker, IconPicker, DEFAULT_EMPLOYEE_COLOR, DEFAULT_EMPLOYEE_ICON} from "./Employee/employeeAppearance";
import useAttendanceSettings from "../Attendance/useAttendanceSettings";
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";
import AppleSelect from "../Common/AppleSelect";
import Button from "../Common/Button";

const SpinningLoader = (props) => <Loader {...props} className={`${props.className} animate-spin`}/>;

// Photo paths ("/uploads/employees/…") are served by the API host, outside /api
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

const EmployeeEditor = ({employee, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: employee ? employee.name : '',
        email: employee ? employee.email : '',
        password: '',
        active: employee ? employee.active : true,
        // Visible in the booking list unless explicitly hidden (older employees have no value).
        showOnCalendar: employee ? employee.showOnCalendar !== false : true,
        profileId: employee && employee.profil ? String(employee.profil.id) : '',
        color: employee && employee.color ? employee.color : DEFAULT_EMPLOYEE_COLOR,
        icon: employee && employee.icon ? employee.icon : DEFAULT_EMPLOYEE_ICON,
        // Service categories performed — [] = all (online booking only offers qualified employees)
        serviceCategoryIds: employee && Array.isArray(employee.serviceCategoryIds) ? employee.serviceCategoryIds : [],
        // N° on the time clock (Hikvision employeeNo). Empty on creation = next free number.
        badgeNumber: employee && employee.badgeNumber != null ? String(employee.badgeNumber) : '',
        // Online booking: shown under the name when the client picks a specialist first
        jobTitle: employee && employee.jobTitle ? employee.jobTitle : '',
        // Online booking: shown on the specialist's profile
        bio: employee && employee.bio ? employee.bio : '',
        // Online booking: told about their new appointments on WhatsApp
        phone: employee && employee.phone ? employee.phone : ''
    });
    // Online booking photo: a new file to upload, or removal of the current one
    const initialPhoto = employee && employee.photoPath ? `${API_ORIGIN}${employee.photoPath}` : null;
    const [photoPreview, setPhotoPreview] = useState(initialPhoto);
    const [photoFile, setPhotoFile] = useState(null);
    const photoInputRef = useRef(null);
    const initialBadge = employee && employee.badgeNumber != null ? String(employee.badgeNumber) : '';

    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showBackofficeModal, setShowBackofficeModal] = useState(false);
    const [showQuickcom360Modal, setShowQuickcom360Modal] = useState(false);
    const queryClient = useQueryClient();
    const {enabled: attendanceEnabled} = useAttendanceSettings();

    const {data: profiles, isLoading: loadingProfiles} = useQuery({
        queryKey: ['profiles'],
        queryFn: async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const response = await api.get('/profils', {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json'
                    }
                });
                return response.data || [];
            } catch (error) {
                console.error("Error fetching profiles:", error);
                return [];
            }
        },
        staleTime: 5 * 60 * 1000
    });

    const {data: categories, isLoading: loadingCategories} = useQuery({
        queryKey: ['categories-all'],
        queryFn: async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const response = await api.get('/categories?page=1&itemsPerPage=500', {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json'
                    }
                });
                return response.data?.data || response.data || [];
            } catch (error) {
                console.error("Error fetching categories:", error);
                return [];
            }
        },
        staleTime: 5 * 60 * 1000
    });

    const toggleCategory = (id) => {
        setFormData(prev => ({
            ...prev,
            serviceCategoryIds: prev.serviceCategoryIds.includes(id)
                ? prev.serviceCategoryIds.filter(c => c !== id)
                : [...prev.serviceCategoryIds, id]
        }));
    };

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: null}));
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            showNotification('error', 'Format accepté : JPEG, PNG ou WebP');
            return;
        }
        if (file.size > MAX_PHOTO_SIZE) {
            showNotification('error', 'La photo ne doit pas dépasser 5 Mo');
            return;
        }
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        if (photoInputRef.current) photoInputRef.current.value = '';
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom de l'employé est requis";
        }

        if (!formData.email.trim()) {
            newErrors.email = "L'adresse email est requise";
        }

        if (!employee && !formData.password.trim()) {
            newErrors.password = "Le mot de passe est requis pour un nouvel employé";
        } else if (formData.password.trim() && formData.password.length !== 4) {
            newErrors.password = "Le mot de passe doit contenir exactement 4 caractères";
        }

        if (!formData.profileId || formData.profileId === '') {
            newErrors.profileId = "Le profil est requis";
        }

        if (formData.badgeNumber.trim() && !/^[1-9]\d{0,7}$/.test(formData.badgeNumber.trim())) {
            newErrors.badgeNumber = "Le n° de badge doit être un nombre entre 1 et 99999999";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const payload = {
                name: formData.name,
                email: formData.email,
                active: formData.active,
                // The API's employee processor reads the snake_case key
                show_on_calendar: formData.showOnCalendar,
                color: formData.color,
                icon: formData.icon,
                serviceCategoryIds: formData.serviceCategoryIds,
                job_title: formData.jobTitle.trim(),
                bio: formData.bio.trim(),
                phone: formData.phone.trim()
            };

            if (formData.password.trim()) {
                payload.password = formData.password;
            }

            // Only sent when changed: absent = unchanged (and auto-numbered on creation), "" = removed.
            if (formData.badgeNumber.trim() !== initialBadge) {
                payload.badge_number = formData.badgeNumber.trim();
            }

            if (formData.profileId) {
                payload.profileId = `/api/profils/${formData.profileId}`;
            }

            let response;

            if (employee) {
                response = await api.put(`/employees/${employee.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Employé mis à jour avec succès');
            } else {
                response = await api.post('/employees', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Employé créé avec succès');
            }

            // Photo: separate multipart endpoint, once the employee exists
            const employeeId = employee ? employee.id : response?.data?.id;
            if (employeeId && photoFile) {
                const data = new FormData();
                data.append('photo', photoFile);
                await api.post(`/employees/${employeeId}/photo`, data, {
                    // The api instance defaults to JSON, which makes axios serialize the FormData (file lost)
                    headers: {'X-account-id': accountId.id, 'Content-Type': 'multipart/form-data'}
                });
            } else if (employeeId && initialPhoto && !photoPreview) {
                await api.delete(`/employees/${employeeId}/photo`, {
                    headers: {'X-account-id': accountId.id}
                });
            }

            queryClient.invalidateQueries({queryKey: ['employees']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.['hydra:description'] ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    const accessRow = (title, text, Icon, onOpen) => (
        <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{text}</p>
            </div>
            <Button variant="tinted" size="sm" icon={Icon} onClick={onOpen}>
                Gérer l'accès
            </Button>
        </div>
    );

    return (
        <>
            <Modal
                title={employee ? 'Modifier un employé' : 'Nouvel employé'}
                subtitle={employee?.name}
                onClose={onClose}
                footer={<ModalFooter onCancel={onClose} form="employee-form" saving={submitting}
                                     label={employee ? 'Enregistrer' : 'Créer'}/>}
            >
                <form id="employee-form" onSubmit={handleSubmit} className="space-y-4">
                    <FormField id="name" label="Nom de l'employé" required error={errors.name}>
                        <div className="relative">
                            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`${inputClass(errors.name)} pl-9`}
                                placeholder="Nom de l'employé"
                            />
                        </div>
                    </FormField>

                    <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Apparence de l'avatar :</span>
                        <div className="flex items-center gap-3">
                            <MiniColorPicker
                                value={formData.color}
                                onChange={(c) => setFormData(prev => ({...prev, color: c}))}
                            />
                            <IconPicker
                                value={formData.icon}
                                color={formData.color}
                                onChange={(i) => setFormData(prev => ({...prev, icon: i}))}
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400">Couleur et icône affichées sur la tablette et le backoffice</span>
                        </div>
                    </div>

                    <FormField id="jobTitle" label="Poste" hint="Affiché sous le nom sur la réservation en ligne">
                        <div className="relative">
                            <Briefcase className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input
                                type="text"
                                id="jobTitle"
                                name="jobTitle"
                                maxLength={100}
                                value={formData.jobTitle}
                                onChange={handleChange}
                                className={`${inputClass(false)} pl-9`}
                                placeholder="ex: Coiffeur Barbier"
                            />
                        </div>
                    </FormField>

                    <FormField id="phone" label="WhatsApp" hint="Numéro marocain, pour le prévenir des rendez-vous pris en ligne. Non visible par les clients.">
                        <div className="relative">
                            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                maxLength={30}
                                value={formData.phone}
                                onChange={handleChange}
                                className={`${inputClass(false)} pl-9`}
                                placeholder="ex: 06 12 34 56 78"
                            />
                        </div>
                    </FormField>

                    <FormField id="bio" label="Présentation" hint={`Affichée sur la fiche du spécialiste, visible par les clients sur la réservation en ligne (${formData.bio.length}/500)`}>
                        <textarea
                            id="bio"
                            name="bio"
                            rows={3}
                            maxLength={500}
                            value={formData.bio}
                            onChange={handleChange}
                            className={inputClass(false)}
                            placeholder="ex: Spécialiste des dégradés et de la barbe, 10 ans d'expérience"
                        />
                    </FormField>

                    <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Photo :</span>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <button type="button" onClick={() => photoInputRef.current?.click()}
                                        aria-label={photoPreview ? 'Changer la photo' : 'Ajouter une photo'}
                                        className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary transition-colors">
                                    {photoPreview
                                        ? <img src={photoPreview} alt="" className="w-full h-full object-cover"/>
                                        : <Camera className="w-5 h-5 text-gray-400"/>}
                                </button>
                                {photoPreview && (
                                    <button type="button" onClick={removePhoto} aria-label="Retirer la photo"
                                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                                        <X size={12}/>
                                    </button>
                                )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Affichée sur la réservation en ligne quand le client choisit son spécialiste (JPEG, PNG ou WebP, 5 Mo max)
                            </span>
                            <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                                   onChange={handlePhotoChange} className="hidden"/>
                        </div>
                    </div>

                    <FormField id="email" label="Email" required error={errors.email}>
                        <div className="relative">
                            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`${inputClass(errors.email)} pl-9`}
                                placeholder="Email"
                            />
                        </div>
                    </FormField>

                    <FormField id="password" label="Mot de passe PIN" required={!employee} error={errors.password}
                               hint={employee ? 'Laisser vide pour ne pas le modifier.' : undefined}>
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`${inputClass(errors.password)} pl-9 pr-10`}
                                placeholder="4 chiffres"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? 'Masquer le PIN' : 'Afficher le PIN'}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                            </button>
                        </div>
                    </FormField>

                    {/* N° de badge pointeuse (module Pointage actif uniquement) */}
                    {attendanceEnabled && (
                        <div>
                            <label htmlFor="badgeNumber" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                N° de badge pointeuse :
                            </label>
                            <div className="relative">
                                <Fingerprint className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    id="badgeNumber"
                                    name="badgeNumber"
                                    value={formData.badgeNumber}
                                    onChange={handleChange}
                                    className={`${inputClass(errors.badgeNumber)} pl-9`}
                                    placeholder={employee ? "Aucun" : "Automatique"}
                                />
                            </div>
                            {errors.badgeNumber
                                ? <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.badgeNumber}</p>
                                : initialBadge && formData.badgeNumber.trim() !== initialBadge
                                    ? <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                        Changer le n° oblige à réenregistrer l'empreinte et le visage sur la pointeuse (utilisateur n° {initialBadge}).
                                      </p>
                                    : <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        À saisir comme « N° employé » lors de l'enregistrement sur la pointeuse
                                      </p>}
                        </div>
                    )}

                    <FormField id="profileId" label="Profil" required error={errors.profileId}>
                        <AppleSelect
                            id="profileId"
                            name="profileId"
                            value={formData.profileId}
                            onChange={handleChange}
                            error={errors.profileId}
                            disabled={loadingProfiles}
                            icon={loadingProfiles ? SpinningLoader : Shield}
                            placeholder={loadingProfiles ? "Chargement…" : "Sélectionner un profil"}
                            options={(profiles || []).map(profile => ({
                                value: profile.id,
                                label: profile.name?.replace('ROLE_', '') || profile.label || '',
                            }))}
                        />
                    </FormField>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                        <SwitchRow id="active" label="Actif" checked={formData.active}
                                   onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
                        <SwitchRow id="showOnCalendar" label="Afficher dans la réservation"
                                   description="L'employé apparaît dans la liste des employés disponibles à la réservation"
                                   checked={formData.showOnCalendar}
                                   onChange={(checked) => setFormData(prev => ({...prev, showOnCalendar: checked}))}/>
                    </div>

                    {/* Prestations réalisées (catégories) */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <span className="min-w-0">
                                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prestations réalisées :</span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {formData.serviceCategoryIds.length === 0
                                        ? "Aucune sélection : l'employé est proposé pour toutes les prestations à la réservation"
                                        : "L'employé n'est proposé à la réservation que pour ces catégories"}
                                </span>
                            </span>
                            {formData.serviceCategoryIds.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({...prev, serviceCategoryIds: []}))}
                                    className="text-xs font-medium text-primary hover:underline whitespace-nowrap"
                                >
                                    Toutes
                                </button>
                            )}
                        </div>
                        {loadingCategories ? (
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 py-2">
                                <Loader className="w-4 h-4 animate-spin"/> Chargement des catégories…
                            </div>
                        ) : (categories || []).length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 py-2">Aucune catégorie dans le catalogue.</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {categories.map(category => {
                                    const selected = formData.serviceCategoryIds.includes(category.id);
                                    return (
                                        <button
                                            key={category.id}
                                            type="button"
                                            onClick={() => toggleCategory(category.id)}
                                            aria-pressed={selected}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                                                selected
                                                    ? 'bg-primary/10 border-primary text-primary'
                                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                                            }`}
                                        >
                                            {selected
                                                ? <Check className="w-3.5 h-3.5"/>
                                                : <span className="w-2.5 h-2.5 rounded-full" style={{background: category.color || '#e5e7eb'}}/>}
                                            {category.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Accès (uniquement en modification) */}
                    {employee && (
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                            {accessRow('Accès Backoffice', 'Gérer les droits de connexion au backoffice', Monitor, () => setShowBackofficeModal(true))}
                            {accessRow('Accès Quickcom360', "Connexion à l'app mobile, limitée à ses points de vente", Smartphone, () => setShowQuickcom360Modal(true))}
                        </div>
                    )}
                </form>
            </Modal>

            {/* Modal Accès Backoffice — monté au-dessus (z-index 60) */}
            {showBackofficeModal && employee && (
                <BackofficeAccessModal
                    employee={employee}
                    onClose={() => setShowBackofficeModal(false)}
                    showNotification={showNotification}
                />
            )}

            {/* Modal Accès Quickcom360 */}
            {showQuickcom360Modal && employee && (
                <Quickcom360AccessModal
                    employee={employee}
                    onClose={() => setShowQuickcom360Modal(false)}
                    showNotification={showNotification}
                />
            )}
        </>
    );
};

export default EmployeeEditor;
